import {
  Camera,
  CheckCircle,
  Edit3,
  Package,
  ScanLine,
  Tag,
  Zap
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { useDevanningState } from '../stores/devanningStore'
import { useScanProgressActions } from '../stores/scanProgressStore'

import QuantityConfirmModal from './QuantityConfirmModal'

import { inboundV2Service } from '@/services'
import { useUIStore } from '@/stores/useUIStore'
import { playErrorSound, playSuccessSound } from '@/utils/audio'
import {
  isDingTalkEnvironment,
  isMobile,
  scanQRCodeInDingTalk
} from '@/utils/dingtalk'

type InputMode = 'scan' | 'manual'

const INPUT_CLASS_BASE =
  'w-full px-4 py-4 text-xl border-2 rounded-xl focus:outline-none transition-all duration-200'
const BUTTON_CLASS_BASE =
  'w-full py-4 rounded-xl font-semibold text-lg transition-all duration-200'

const ScanForm: React.FC = () => {
  const [scanning, setScanning] = useState(false)
  const [scanMode] = useState('mark')
  const [inputMode] = useState<InputMode>('scan')
  const [barcode, setBarcode] = useState('')
  const [showQuantityModal, setShowQuantityModal] = useState(false)
  const [pendingBarcode, setPendingBarcode] = useState('')
  const [batchMode] = useState(true)
  const barcodeInputRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useUIStore()
  const { masterBill } = useDevanningState()
  const { fetchProgress } = useScanProgressActions()

  const getPlaceholder = () =>
    `请${inputMode === 'scan' ? '使用扫描枪扫描' : '手动输入'}${
      scanMode === 'box' ? '箱号' : 'Mark'
    }`
  const isBox = scanMode === 'box'
  const isScan = inputMode === 'scan'
  const shouldShowDingTalkScan = isDingTalkEnvironment() && isMobile() && isScan

  const barcodeSubmit = async () => {
    if (!masterBill) return showError('扫描异常, 没有选择MBL')
    if (scanning) return showWarning('请稍后, 上一次扫描任务还未结束')

    if (!isBox && batchMode) {
      setPendingBarcode(barcode)
      setShowQuantityModal(true)
    } else {
      await performScan(barcode)
    }
  }

  const performScan = async (
    barcodeToScan: string,
    quantityToScan?: number,
    manageScanning = true
  ) => {
    if (!masterBill) return showError('扫描异常, 没有选择MBL')
    if (manageScanning) {
      setScanning(true)
    }
    try {
      const { success, message, data } = await inboundV2Service.scan(
        masterBill.id,
        {
          barcode: barcodeToScan,
          quantity: quantityToScan
        }
      )

      if (success) {
        showSuccess(message)
        playSuccessSound()
        setBarcode('')
        setShowQuantityModal(false)
        setPendingBarcode('')
        fetchProgress(data?.markId)
      } else {
        showError(message)
        playErrorSound()
      }
    } catch {
      showError('扫描失败')
      playErrorSound()
    } finally {
      if (manageScanning) {
        setScanning(false)
      }
    }
  }

  const handleQuantityConfirm = (quantity: number) =>
    performScan(pendingBarcode, quantity)
  const handleQuantityCancel = () => {
    setShowQuantityModal(false)
    setPendingBarcode('')
  }

  const handleDingTalkScan = async () => {
    if (scanning) return showWarning('请稍后, 上一次扫描任务还未结束')
    if (!masterBill) return showError('扫描异常, 没有选择MBL')
    setScanning(true)
    try {
      const scanResult = await scanQRCodeInDingTalk()
      setBarcode(scanResult)
      showSuccess('扫码成功')
      if (!isBox && batchMode) {
        setPendingBarcode(scanResult)
        setShowQuantityModal(true)
      } else {
        await performScan(scanResult, undefined, false)
      }
    } catch (error: unknown) {
      const errorMsg = error instanceof Error ? error.message : '扫码失败'
      if (errorMsg !== '用户取消扫描') {
        showError(errorMsg)
        playErrorSound()
      }
    } finally {
      setScanning(false)
    }
  }

  // 更新
  const barcodeInputHandle = (value: string) => {
    setBarcode(value)
  }
  const barcodeKeyDownHandle = (value: string) => {
    if (value === 'Enter') {
      barcodeSubmit()
    }
  }

  useEffect(() => barcodeInputRef.current?.focus(), [])
  useEffect(() => {
    if (!scanning && !showQuantityModal && barcodeInputRef.current) {
      barcodeInputRef.current.focus()
      barcodeInputRef.current.select()
    }
  }, [scanning, showQuantityModal])

  return (
    <>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="space-y-3">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              {isScan ? (
                <ScanLine className="w-6 h-6 text-green-500 mr-2" />
              ) : (
                <Edit3 className="w-6 h-6 text-purple-500 mr-2" />
              )}
              {isScan ? '扫描条码' : '手动输入'}
            </h2>
          </div>
        </div>

        <div className="p-6 space-y-3">
          <div className="relative">
            <div className="flex items-center space-x-2 mb-2">
              {isBox ? (
                <Package className="w-8 h-8 text-blue-500" />
              ) : (
                <Tag className="w-8 h-8 text-orange-500" />
              )}
              <div className="flex-1">
                <span className="text-lg font-medium text-gray-700">
                  {getPlaceholder()}
                </span>
                {!isBox && batchMode && (
                  <div className="text-sm text-amber-600 mt-1 flex items-center">
                    <Zap className="w-3 h-3 mr-1" />
                    批量模式已启用，扫描后将弹出数量确认
                  </div>
                )}
              </div>
            </div>
            <input
              ref={barcodeInputRef}
              type="text"
              value={barcode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                barcodeInputHandle(e.target.value.trim())
              }
              placeholder={isScan ? '扫描枪扫描中...' : getPlaceholder()}
              disabled={scanning}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                barcodeKeyDownHandle(e.key)
              }
              className={`${INPUT_CLASS_BASE} ${
                scanning
                  ? 'border-yellow-300 bg-yellow-50'
                  : isScan
                  ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                  : barcode.trim()
                  ? isBox
                    ? 'border-blue-300 bg-blue-50 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                    : 'border-orange-300 bg-orange-50 focus:border-orange-500 focus:ring-4 focus:ring-orange-100'
                  : isBox
                  ? 'border-gray-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100'
                  : 'border-gray-300 focus:border-orange-500 focus:ring-4 focus:ring-orange-100'
              }`}
            />
            {scanning && (
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-yellow-500 border-t-transparent"></div>
              </div>
            )}
          </div>

          {!isScan && (
            <div className="space-y-3">
              <button
                disabled={scanning || !barcode.trim()}
                onClick={barcodeSubmit}
                className={`${BUTTON_CLASS_BASE} ${
                  scanning
                    ? 'bg-yellow-500 text-white'
                    : barcode.trim()
                    ? isBox
                      ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                      : 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {scanning ? (
                  <span className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                    扫描中...
                  </span>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="w-5 h-5" />
                    <span>确认提交</span>
                  </div>
                )}
              </button>
            </div>
          )}

          {isScan && (
            <div className="text-center">
              <div className="text-gray-500 text-sm flex items-center justify-center">
                <ScanLine className="w-4 h-4 mr-2" />
                使用扫描枪扫描后会自动处理
              </div>
              {shouldShowDingTalkScan && (
                <button
                  onClick={handleDingTalkScan}
                  disabled={scanning}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2 mx-auto ${
                    scanning
                      ? 'bg-yellow-500 text-white cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                  }`}
                >
                  {scanning ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>扫描中...</span>
                    </>
                  ) : (
                    <>
                      <Camera className="w-4 h-4" />
                      <span>调用摄像头扫描</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <QuantityConfirmModal
        visible={showQuantityModal}
        barcode={pendingBarcode}
        scanning={scanning}
        onConfirm={handleQuantityConfirm}
        onCancel={handleQuantityCancel}
      />
    </>
  )
}

export default ScanForm
